import React, { useState } from 'react';
import { Heart, MessageCircle, Trash2, AlertCircle } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { useUser } from '../contexts/UserContext';
import CommentDialog from './CommentDialog';
import DeleteConfirmDialog from './DeleteConfirmDialog';

interface PostActionsProps {
  postId: string;
  userId: string;
  likes: number;
  comments: number;
  liked: boolean;
  onLikeChange: () => void;
  onDelete?: () => void;
}

function PostActions({ postId, userId, likes, comments, liked, onLikeChange, onDelete }: PostActionsProps) {
  const { user } = useUser();
  const [showComments, setShowComments] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const isOwner = user?.id === userId;

  const handleLike = async () => {
    if (!user) return;

    try {
      if (liked) {
        await supabase
          .from('likes')
          .delete()
          .eq('post_id', postId)
          .eq('user_id', user.id);
      } else {
        await supabase
          .from('likes')
          .insert({
            post_id: postId,
            user_id: user.id
          });

        // Create notification for post owner if it's not their own post
        if (userId !== user.id) {
          await supabase
            .from('notifications')
            .insert({
              user_id: userId,
              type: 'like',
              actor_id: user.id,
              post_id: postId
            });
        }
      }

      onLikeChange();
    } catch (error) {
      console.error('Error toggling like:', error);
    }
  };

  const handleDelete = async () => {
    if (!user || !isOwner || deleting) return;

    setDeleting(true);
    try {
      const { error } = await supabase
        .from('posts')
        .delete()
        .eq('id', postId)
        .eq('user_id', user.id)
        .single();

      if (error) throw error;

      setShowDeleteConfirm(false);
      if (onDelete) {
        onDelete();
      }
    } catch (error) {
      console.error('Error deleting post:', error);
      alert('Failed to delete post. Please try again.');
    } finally {
      setDeleting(false);
      setShowDeleteConfirm(false);
    }
  };

  const handleShare = async () => {
    try {
      const shareData = {
        title: 'Check out this post on Music Compare',
        text: 'Check out this music post!',
        url: `${window.location.origin}/post/${postId}`
      };

      if (navigator.share && navigator.canShare(shareData)) {
        await navigator.share(shareData);
      } else {
        await navigator.clipboard.writeText(shareData.url);
        alert('Link copied to clipboard!');
      }
    } catch (error) {
      console.error('Error sharing:', error);
      // If sharing fails, fallback to copying the link
      try {
        await navigator.clipboard.writeText(`${window.location.origin}/post/${postId}`);
        alert('Link copied to clipboard!');
      } catch (clipboardError) {
        console.error('Error copying to clipboard:', clipboardError);
        alert('Failed to share post');
      }
    }
  };

  return (
    <>
      <div className="flex items-center justify-between mt-4">
        <div className="flex items-center space-x-4 text-gray-500">
          <button 
            onClick={handleLike}
            className={`flex items-center space-x-1 transition-colors ${
              liked ? 'text-red-500' : 'hover:text-red-500'
            }`}
          >
            <Heart className={`h-5 w-5 ${liked ? 'fill-current' : ''}`} />
            <span>{likes}</span>
          </button>
          <button 
            onClick={() => setShowComments(true)}
            className="flex items-center space-x-1 hover:text-indigo-600 transition-colors"
          >
            <MessageCircle className="h-5 w-5" />
            <span>{comments}</span>
          </button>
          <button 
            onClick={handleShare}
            className="flex items-center space-x-1 hover:text-indigo-600 transition-colors"
          >
            <svg 
              viewBox="0 0 24 24" 
              fill="none"
              stroke="currentColor" 
              strokeWidth="2" 
              strokeLinecap="round" 
              strokeLinejoin="round"
              className="h-5 w-5"
            >
              <path d="M21 12 H8 M21 12 L15 6 M21 12 L15 18" />
            </svg>
          </button>
        </div>

        {isOwner && (
          <button
            onClick={() => setShowDeleteConfirm(true)}
            className="p-2 text-gray-500 hover:text-red-500 transition-colors"
            aria-label="Delete post"
          >
            <Trash2 className="h-5 w-5" />
          </button>
        )}
      </div>

      <CommentDialog
        isOpen={showComments}
        onClose={() => setShowComments(false)}
        postId={postId}
      />

      <DeleteConfirmDialog
        isOpen={showDeleteConfirm}
        onClose={() => setShowDeleteConfirm(false)}
        onConfirm={handleDelete}
        deleting={deleting}
        title="Delete Post"
        message="Are you sure you want to delete this post? This action cannot be undone."
      />
    </>
  );
}

export default PostActions;